import { useState } from 'react';
import { Layout, Database, GitBranch, MousePointerClick, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, ERROR, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import { usePlanStore } from '@/stores/ai/plan-store';
import { PlanAuditResultCard } from './PlanAuditResultCard';
import { PlanResearchPanel } from './PlanResearchPanel';
import { PLAN_DOMAIN_ICONS, PLAN_DOMAIN_LABELS } from '@/types/plan';
import type {
  Plan, Roadmap, PhasePrompt, AuditResult,
  PlanResearchContext, PlanSectionDomain,
} from '@/types/plan';
import type { LucideIcon } from 'lucide-react';

const DOMAIN_ICON_COMPONENTS: Record<string, LucideIcon> = { Layout, Database, GitBranch, MousePointerClick };

function getDomainIcon(domain: PlanSectionDomain): LucideIcon {
  const iconName = PLAN_DOMAIN_ICONS[domain];
  return DOMAIN_ICON_COMPONENTS[iconName] ?? Layout;
}

const COMPLEXITY_CALL_MAP: Record<string, number> = { low: 1, medium: 2, high: 3 };
const COST_PER_CALL = 0.04;

// ── Quick Mode Props ──────────────────────────────────────────────

interface PlanReviewCardQuickProps {
  mode: 'quick';
  plan: Plan;
  onExecute: () => void;
  onModify: () => void;
  onDiscard: () => void;
}

// ── Full Mode Props ───────────────────────────────────────────────

interface PlanReviewCardFullProps {
  mode: 'full';
  roadmap?: Roadmap;
  phases: PhasePrompt[];
  phaseAudits: Record<string, AuditResult>;
  researchContext?: PlanResearchContext;
  onExecuteAll: () => void;
  onExecutePhase: (phaseId: string) => void;
  onModifyPhase: (phaseId: string) => void;
  onModifyPhaseCriterion?: (phaseId: string, criterionName: string) => void;
  onDiscard: () => void;
}

type PlanReviewCardProps = PlanReviewCardQuickProps | PlanReviewCardFullProps;

// ── Component ─────────────────────────────────────────────────────

export function PlanReviewCard(props: PlanReviewCardProps) {
  if (props.mode === 'quick') return <QuickModeReview {...props} />;
  return <FullModeReview {...props} />;
}

// ── Quick Mode ────────────────────────────────────────────────────

function QuickModeReview({ plan, onExecute, onModify, onDiscard }: PlanReviewCardQuickProps) {
  const sections = Array.isArray(plan.sections) ? plan.sections : [];
  return (
    <div style={{ border: `1px solid ${BORDER.DEFAULT}`, borderRadius: 8, background: WHITE, boxShadow: SHADOWS.sm, padding: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Plan Review</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        {sections.map((section) => {
          const DomainIcon = getDomainIcon(section.domain);
          return (
            <details key={section.id}>
              <summary className="flex items-center gap-2" style={{ cursor: 'pointer', padding: '6px 0', fontSize: 13, fontWeight: 500 }}>
                <DomainIcon style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
                {PLAN_DOMAIN_LABELS[section.domain]}
              </summary>
              <p style={{ fontSize: 12, color: MID_GRAY.DEFAULT, margin: '4px 0 4px 22px', lineHeight: 1.5 }}>
                {section.description}
              </p>
            </details>
          );
        })}
      </div>

      <ActionButtons
        primary={{ label: 'Execute', onClick: onExecute }}
        secondary={{ label: 'Modify', onClick: onModify }}
        danger={{ label: 'Discard', onClick: onDiscard }}
      />
    </div>
  );
}

// ── Full Mode ─────────────────────────────────────────────────────

function FullModeReview({
  roadmap,
  phases,
  phaseAudits,
  researchContext,
  onExecuteAll,
  onExecutePhase,
  onModifyPhase,
  onModifyPhaseCriterion,
  onDiscard,
}: PlanReviewCardFullProps) {
  const editedPrompts = usePlanStore((s) => s.editedPhasePrompts);
  const setEditedPhasePrompt = usePlanStore((s) => s.setEditedPhasePrompt);
  const [expandedPhase, setExpandedPhase] = useState<string | null>(null);
  const [editingPhase, setEditingPhase] = useState<string | null>(null);
  const [showResearch, setShowResearch] = useState(false);

  const togglePhase = (id: string) => setExpandedPhase((prev) => (prev === id ? null : id));

  return (
    <div style={{ border: `1px solid ${BORDER.DEFAULT}`, borderRadius: 8, background: WHITE, boxShadow: SHADOWS.sm, padding: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
        Plan Review — Full Pipeline
      </div>

      {/* Research summary */}
      {researchContext && researchContext.findings.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="flex items-center gap-1.5"
            onClick={() => setShowResearch(!showResearch)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              color: FOREST_GREEN.DEFAULT,
              padding: '4px 0',
            }}
          >
            {showResearch ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            Research: {researchContext.findings.length} findings
          </button>
          {showResearch && (
            <div style={{ marginTop: 8 }}>
              <PlanResearchPanel researchContext={researchContext} isLoading={false} />
            </div>
          )}
        </div>
      )}

      {/* Roadmap summary */}
      {roadmap && (
        <div style={{ fontSize: 12, color: MID_GRAY.DEFAULT, marginBottom: 12, paddingLeft: 4 }}>
          Roadmap: {roadmap.phases.length} phases
          {roadmap.estimatedCost != null && `, ~$${roadmap.estimatedCost.toFixed(2)}`}
        </div>
      )}

      {/* Phase prompts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: MID_GRAY.DEFAULT, marginBottom: 4 }}>Phase Prompts:</div>

        {phases.map((phase) => {
          const DomainIcon = getDomainIcon(phase.domain);
          const audit = phaseAudits[phase.phaseId];
          const isExpanded = expandedPhase === phase.id;
          const isEditing = editingPhase === phase.id;
          const hasEdits = editedPrompts[phase.id] != null;

          // Cost estimate from roadmap
          const roadmapPhase = roadmap?.phases.find((rp) => rp.id === phase.phaseId);
          const callCount = roadmapPhase ? (COMPLEXITY_CALL_MAP[roadmapPhase.estimatedComplexity] ?? 1) : 1;
          const phaseCost = callCount * COST_PER_CALL;

          return (
            <div key={phase.id}>
              <div className="flex items-center gap-2" style={{ padding: '6px 0' }}>
                <button
                  type="button"
                  className="flex items-center gap-2 flex-1"
                  onClick={() => togglePhase(phase.id)}
                  aria-expanded={isExpanded}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0, fontSize: 13 }}
                >
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5" style={{ color: MID_GRAY.DEFAULT }} />
                    : <ChevronRight className="w-3.5 h-3.5" style={{ color: MID_GRAY.DEFAULT }} />}
                  <DomainIcon style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
                  <span style={{ fontWeight: 500 }}>
                    Phase {phase.phaseNumber}: {phase.title}
                    {hasEdits && <span style={{ color: FOREST_GREEN.DEFAULT, marginLeft: 4 }}>*</span>}
                  </span>
                </button>

                {audit && <PlanAuditResultCard auditResult={audit} auditType="" compact />}

                <button
                  type="button"
                  onClick={() => setEditingPhase(isEditing ? null : phase.id)}
                  aria-label={`Edit phase ${phase.phaseNumber} prompt`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                >
                  <Pencil className="w-3.5 h-3.5" style={{ color: MID_GRAY.lighter }} />
                </button>
              </div>

              {/* Cost estimate */}
              <div style={{ fontSize: 11, color: MID_GRAY.lighter, paddingLeft: 28 }}>
                ~{callCount} LLM call{callCount !== 1 ? 's' : ''} (~${phaseCost.toFixed(2)})
              </div>

              {/* Expanded audit detail + prompt preview */}
              {isExpanded && !isEditing && (
                <div style={{ margin: '4px 0 8px 28px' }}>
                  {audit && (
                    <div style={{ marginBottom: 8 }}>
                      <PlanAuditResultCard
                        auditResult={audit}
                        auditType={`Phase ${phase.phaseNumber} Audit`}
                        onImproveCriterion={onModifyPhaseCriterion
                          ? (criterionName) => onModifyPhaseCriterion(phase.phaseId, criterionName)
                          : undefined}
                      />
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: MID_GRAY.DEFAULT, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {(editedPrompts[phase.id] ?? phase.executionPrompt).slice(0, 300)}
                    {phase.executionPrompt.length > 300 && '...'}
                  </div>
                </div>
              )}

              {/* Inline editor */}
              {isEditing && (
                <div style={{ margin: '4px 0 8px 28px' }}>
                  <textarea
                    value={editedPrompts[phase.id] ?? phase.executionPrompt}
                    onChange={(e) => setEditedPhasePrompt(phase.id, e.target.value)}
                    rows={6}
                    style={{
                      width: '100%',
                      fontSize: 12,
                      lineHeight: 1.5,
                      padding: 8,
                      borderRadius: 4,
                      border: `1px solid ${BORDER.DEFAULT}`,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={onExecuteAll}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: 'none',
            background: FOREST_GREEN.DEFAULT,
            color: WHITE,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Execute All
        </button>

        {/* Execute Phase dropdown */}
        <details style={{ position: 'relative' }}>
          <summary
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${BORDER.DEFAULT}`,
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              listStyle: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            Execute Phase <ChevronDown className="w-3.5 h-3.5" />
          </summary>
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: WHITE,
              border: `1px solid ${BORDER.DEFAULT}`,
              borderRadius: 6,
              boxShadow: SHADOWS.sm,
              zIndex: 10,
              minWidth: 180,
            }}
          >
            {phases.map((phase) => (
              <button
                key={phase.id}
                type="button"
                onClick={() => onExecutePhase(phase.phaseId)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 12px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Phase {phase.phaseNumber}: {phase.title}
              </button>
            ))}
          </div>
        </details>

        {/* Modify Phase dropdown */}
        <details style={{ position: 'relative' }}>
          <summary
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${BORDER.DEFAULT}`,
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              listStyle: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            Modify Phase <ChevronDown className="w-3.5 h-3.5" />
          </summary>
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: WHITE,
              border: `1px solid ${BORDER.DEFAULT}`,
              borderRadius: 6,
              boxShadow: SHADOWS.sm,
              zIndex: 10,
              minWidth: 180,
            }}
          >
            {phases.map((phase) => (
              <button
                key={phase.id}
                type="button"
                onClick={() => onModifyPhase(phase.phaseId)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 12px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Phase {phase.phaseNumber}: {phase.title}
              </button>
            ))}
          </div>
        </details>

        <button
          type="button"
          onClick={onDiscard}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: 'none',
            background: 'transparent',
            color: ERROR.DEFAULT,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}

// ── Shared Action Buttons ─────────────────────────────────────────

function ActionButtons({
  primary,
  secondary,
  danger,
}: {
  primary: { label: string; onClick: () => void };
  secondary: { label: string; onClick: () => void };
  danger: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={primary.onClick}
        style={{
          padding: '8px 20px',
          borderRadius: 6,
          border: 'none',
          background: FOREST_GREEN.DEFAULT,
          color: WHITE,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        {primary.label}
      </button>
      <button
        type="button"
        onClick={secondary.onClick}
        style={{
          padding: '8px 20px',
          borderRadius: 6,
          border: `1px solid ${BORDER.DEFAULT}`,
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        {secondary.label}
      </button>
      <button
        type="button"
        onClick={danger.onClick}
        style={{
          padding: '8px 20px',
          borderRadius: 6,
          border: 'none',
          background: 'transparent',
          color: ERROR.DEFAULT,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        {danger.label}
      </button>
    </div>
  );
}
